import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface DashboardMetrics {
  totalClientes: number;
  totalSistemas: number;
  nuevasAltasHoy: number;
  checklistsHoy: number;
  sistemasPorTipo: Array<{
    tipo: string;
    cantidad: number;
    porcentaje: number;
  }>;
  actividadSemanal: Array<{
    fecha: string;
    clientes: number;
    mandatos: number;
    checklists: number;
  }>;
  actividadReciente: Array<{
    id: string;
    tipo: 'cliente' | 'mandato' | 'checklist';
    descripcion: string;
    fecha: string;
  }>;
}

export const useDashboardMetrics = () => {
  const [metrics, setMetrics] = useState<DashboardMetrics>({
    totalClientes: 0,
    totalSistemas: 0,
    nuevasAltasHoy: 0,
    checklistsHoy: 0,
    sistemasPorTipo: [],
    actividadSemanal: [],
    actividadReciente: []
  });
  const [loading, setLoading] = useState(true);

  const fetchMetrics = async () => {
    try {
      const hoy = new Date().toISOString().split('T')[0];
      const semanaAtras = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();

      // Total clientes
      const { count: totalClientes } = await supabase
        .from('clientes')
        .select('*', { count: 'exact', head: true });

      // Total sistemas
      const { count: totalSistemas } = await supabase
        .from('sistemas')
        .select('*', { count: 'exact', head: true });

      // Sistemas por tipo
      const { data: sistemasData } = await supabase
        .from('sistemas')
        .select('tipo');

      const sistemasPorTipo = sistemasData?.reduce((acc: Record<string, number>, sistema) => {
        acc[sistema.tipo] = (acc[sistema.tipo] || 0) + 1;
        return acc;
      }, {}) || {};

      const sistemasPorTipoArray = Object.entries(sistemasPorTipo).map(([tipo, cantidad]) => ({
        tipo,
        cantidad: cantidad as number,
        porcentaje: totalSistemas ? Math.round(((cantidad as number) / totalSistemas) * 100) : 0
      }));

      // Nuevas altas hoy (clientes + mandatos)
      const { count: clientesHoy } = await supabase
        .from('clientes')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', hoy);

      const { count: mandatosHoy } = await supabase
        .from('mandatos_sepa')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', hoy);

      // Checklists hoy
      const { count: checklistsHoy } = await supabase
        .from('checklists_seguridad')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', hoy);

      // Actividad reciente
      const { data: clientesRecientes } = await supabase
        .from('clientes')
        .select('id, nombre, created_at')
        .order('created_at', { ascending: false })
        .limit(5);

      const { data: mandatosRecientes } = await supabase
        .from('mandatos_sepa')
        .select('id, nombre_titular, created_at')
        .order('created_at', { ascending: false })
        .limit(5);

      const { data: checklistsRecientes } = await supabase
        .from('checklists_seguridad')
        .select('id, created_at, clientes(nombre)')
        .order('created_at', { ascending: false })
        .limit(5);

      // Combinar y ordenar actividad reciente
      const actividadReciente = [
        ...(clientesRecientes?.map(c => ({
          id: c.id,
          tipo: 'cliente' as const,
          descripcion: `Cliente ${c.nombre} registrado`,
          fecha: c.created_at
        })) || []),
        ...(mandatosRecientes?.map(m => ({
          id: m.id,
          tipo: 'mandato' as const,
          descripcion: `Mandato SEPA de ${m.nombre_titular}`,
          fecha: m.created_at
        })) || []),
        ...(checklistsRecientes?.map(ch => ({
          id: ch.id,
          tipo: 'checklist' as const,
          descripcion: `Checklist para ${ch.clientes?.nombre || 'cliente'}`,
          fecha: ch.created_at
        })) || [])
      ].sort((a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime()).slice(0, 10);

      setMetrics({
        totalClientes: totalClientes || 0,
        totalSistemas: totalSistemas || 0,
        nuevasAltasHoy: (clientesHoy || 0) + (mandatosHoy || 0),
        checklistsHoy: checklistsHoy || 0,
        sistemasPorTipo: sistemasPorTipoArray,
        actividadSemanal: [], // TODO: Implementar datos semanales
        actividadReciente
      });
    } catch (error) {
      console.error('Error fetching metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMetrics();
  }, []);

  return { metrics, loading, refetch: fetchMetrics };
};